import type { CSSProperties } from 'react';
import { extractFilenameFromUrl } from '@/entities/conversations';
import { sanitizeHtml } from '@/shared/lib';

/** Наибольшая сторона (px) не загрузившейся картинки, которая считается мелкой */
const SMALL_IMAGE_MAX_SIDE = 150;

/** Элементы без содержимого: детей им не передаётся */
const VOID_TAGS = new Set(['area', 'br', 'col', 'embed', 'hr', 'img', 'source', 'track', 'wbr']);

/** Элементы таблицы, внутри которых текст из одних пропусков — отступ разметки, а не содержимое */
const TABLE_STRUCTURE_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'colgroup']);

/** Имена атрибутов HTML, которые React принимает под своими именами */
const REACT_ATTRIBUTE_NAMES: Record<string, string> = {
  colspan: 'colSpan',
  rowspan: 'rowSpan',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  srcset: 'srcSet',
  crossorigin: 'crossOrigin',
  referrerpolicy: 'referrerPolicy',
  usemap: 'useMap',
  datetime: 'dateTime',
  hreflang: 'hrefLang',
};

/**
 * Атрибуты, которые с чужим содержимым на страницу не переносятся: идентификатор совпал бы
 * с идентификаторами страницы, класс письма — с классами её оформления (своих стилей письмо
 * с собой не несёт), а правка и фокус в облаке переписки не нужны. Встроенный стиль
 * переносится отдельно — объектом.
 */
const DROPPED_ATTRIBUTES = new Set(['id', 'name', 'class', 'style', 'contenteditable', 'tabindex', 'autofocus']);

/** Свойства встроенного стиля, которыми содержимое вышло бы за пределы облака */
const DROPPED_STYLE_PROPERTIES = new Set(['position', 'z-index']);

/** Отправитель, дата, получатель и тема пересланного письма */
export type ForwardedMeta = {
  from: string | null;
  sent: string | null;
  to: string | null;
  subject: string | null;
};

/** Поля пересланного письма по их меткам — по-английски и по-русски */
const FORWARDED_META_FIELDS: Record<string, keyof ForwardedMeta> = {
  from: 'from',
  от: 'from',
  sent: 'sent',
  отправлено: 'sent',
  date: 'sent',
  дата: 'sent',
  to: 'to',
  кому: 'to',
  subject: 'subject',
  тема: 'subject',
};

/** Строка поля пересланного письма: метка, двоеточие и значение */
const FORWARDED_META_LINE = /^(From|От|Sent|Отправлено|Date|Дата|To|Кому|Subject|Тема)\s*:\s*(.*)$/i;

/** Элементы, которые начинают новую строку текста */
const LINE_BLOCK_TAGS = new Set(['p', 'div', 'li', 'tr', 'table', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

/** Метка отправителя, с которой начинается блок пересланного письма */
const FORWARDED_SENDER_LABEL = /^(from|от)\s*:?$/i;

/** Не загрузившаяся картинка оформленного содержимого */
export type BrokenContentImage = {
  /** Ключ картинки в облаке */
  key: string;
  /** Адрес картинки — по нему она открывается в новой вкладке */
  src: string;
  /** Имя картинки: последняя часть адреса, а без неё — подпись */
  name: string;
  /** Мелкая картинка: обе стороны заданы и не больше {@link SMALL_IMAGE_MAX_SIDE} */
  isSmall: boolean;
};

/** Оформленное содержимое после очистки — корень разобранного дерева, на страницу не вставленный */
export const parseContentHtml = (html: string): HTMLElement =>
  new DOMParser().parseFromString(sanitizeHtml(html), 'text/html').body;

/** Узел — элемент разметки */
export const isElementNode = (node: Node): node is Element => node.nodeType === Node.ELEMENT_NODE;

/** Элемент, у которого детей нет и быть не может */
export const isVoidElement = (element: Element): boolean => VOID_TAGS.has(element.tagName.toLowerCase());

/** Текст из одних пропусков внутри таблицы — отступ разметки, на экран он не выводится */
export const isTableLayoutWhitespace = (node: Node): boolean =>
  node.nodeType === Node.TEXT_NODE &&
  !node.textContent?.trim() &&
  !!node.parentElement &&
  TABLE_STRUCTURE_TAGS.has(node.parentElement.tagName.toLowerCase());

/** Имя свойства стиля React по имени свойства CSS: `font-size` → `fontSize`, `-webkit-x` → `WebkitX` */
const toReactStyleName = (name: string): string =>
  name.startsWith('--')
    ? name
    : name.replace(/^-ms-/, 'ms-').replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());

/** Встроенный стиль элемента объектом React; свойства, которых браузер не знает, отброшены им же при разборе */
const getElementStyle = (element: Element): CSSProperties | undefined => {
  if (!(element instanceof HTMLElement) || element.style.length === 0) return undefined;
  const style: Record<string, string> = {};
  for (let index = 0; index < element.style.length; index++) {
    const name = element.style.item(index);
    if (DROPPED_STYLE_PROPERTIES.has(name)) continue;
    style[toReactStyleName(name)] = element.style.getPropertyValue(name);
  }
  return style;
};

/**
 * Картинку из письма Word и Outlook оборачивают в элемент с рамкой и отступами — у обёртки
 * картинки они снимаются, чтобы картинка встала в облако без рамки.
 */
const WRAPPED_IMAGE_STYLE: CSSProperties = { border: 'none', padding: 0, outline: 'none' };

/** Свойства элемента React из атрибутов элемента разметки */
export const getElementProps = (element: Element): Record<string, unknown> => {
  const props: Record<string, unknown> = {};
  for (const { name, value } of Array.from(element.attributes)) {
    const attributeName = name.toLowerCase();
    if (DROPPED_ATTRIBUTES.has(attributeName) || attributeName.startsWith('on')) continue;
    props[REACT_ATTRIBUTE_NAMES[attributeName] ?? attributeName] = value;
  }

  const style = getElementStyle(element);
  const wrapsImage = Array.from(element.children).some((child) => child.tagName.toLowerCase() === 'img');
  if (style || wrapsImage) props.style = wrapsImage ? { ...style, ...WRAPPED_IMAGE_STYLE } : style;
  return props;
};

/**
 * Блок пересланного письма: `<div>` с верхней границей цвета `#E1E1E1`, в котором жирным
 * отмечен отправитель («From:» / «От:»).
 */
export const findForwardedBlock = (root: Element): Element | null => {
  for (const block of Array.from(root.getElementsByTagName('div'))) {
    const style = block.getAttribute('style') ?? '';
    if (!/border-top/i.test(style) || !/#e1e1e1/i.test(style)) continue;
    const hasSenderLabel = Array.from(block.getElementsByTagName('b')).some((bold) =>
      FORWARDED_SENDER_LABEL.test(bold.textContent?.trim() ?? ''),
    );
    if (hasSenderLabel) return block;
  }
  return null;
};

/** Строки текста элемента: границы строк — переносы `<br>` и блоки, как их видит читающий */
const getElementLines = (element: Element): string[] => {
  let text = '';
  const collect = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
      return;
    }
    if (!isElementNode(node)) return;
    const tag = node.tagName.toLowerCase();
    if (tag === 'br') {
      text += '\n';
      return;
    }
    node.childNodes.forEach(collect);
    if (LINE_BLOCK_TAGS.has(tag)) text += '\n';
  };
  collect(element);
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
};

/** Поля пересланного письма из его блока: каждое — своей строкой «Метка: значение» */
export const getForwardedMeta = (block: Element): ForwardedMeta => {
  const meta: ForwardedMeta = { from: null, sent: null, to: null, subject: null };
  for (const line of getElementLines(block)) {
    const [, label, value] = line.match(FORWARDED_META_LINE) ?? [];
    const field = label ? FORWARDED_META_FIELDS[label.toLowerCase()] : undefined;
    if (field && value && meta[field] == null) meta[field] = value;
  }
  return meta;
};

/**
 * Тело пересланного письма — всё, что идёт за его блоком в том же родителе, отдельным корнем.
 * Узлы копируются: исходное дерево остаётся целым.
 */
export const extractForwardedBody = (block: Element): HTMLElement => {
  const body = block.ownerDocument.createElement('div');
  let sibling = block.nextSibling;
  while (sibling) {
    body.appendChild(sibling.cloneNode(true));
    sibling = sibling.nextSibling;
  }
  return body;
};

/** Числовое значение атрибута размера картинки; не число — `null` */
const parseImageSide = (value: string | null): number | null => {
  if (value == null) return null;
  const side = Number.parseInt(value, 10);
  return Number.isFinite(side) ? side : null;
};

/** Сведения о не загрузившейся картинке оформленного содержимого */
export const getBrokenContentImage = (image: Element, key: string): BrokenContentImage => {
  const src = image.getAttribute('src') ?? '';
  const width = parseImageSide(image.getAttribute('width'));
  const height = parseImageSide(image.getAttribute('height'));
  return {
    key,
    src,
    name: extractFilenameFromUrl(src) || image.getAttribute('alt')?.trim() || src,
    isSmall: width != null && height != null && width <= SMALL_IMAGE_MAX_SIDE && height <= SMALL_IMAGE_MAX_SIDE,
  };
};
